import React, { useState } from 'react';

import { useApolloClient, ApolloClient } from '@apollo/client';
import { IconCircleX, IconMailForward, IconCircleCheck, IconUnlink } from '@tabler/icons-react';
import dayjs from 'dayjs';
import styled from 'styled-components';
import toast from 'react-hot-toast';

import { TeamPopupWindow } from 'pages/Settings/SettingsPage/SettingsTeam/TeamPopupWindow';
import { AddEmployeesPopupWindow } from 'pages/Settings/SettingsPage/SettingsTeam/AddEmployeesPopupWindow';
import { DetachEmployeeWarningPopup } from 'pages/Settings/SettingsPage/SettingsTeam/DetachEmployeeWarningPopup';
import { EmployeesDropdownButton } from 'pages/Settings/SettingsPage/SettingsTeam/EmployeesDropdownButton';

import { OutsideClickTracker } from 'components/OutsideClickTracker';

import { SettingsCardSubtitle, InvoicesTableStyle, EmployeesTitle } from 'styles/pages/settings.styles';
import { Checkbox } from 'ui/Checkbox';
import { Tooltip, MultilineTooltip } from 'styles';
import { organizationCardUpdatedCacheHandler } from 'utils/cache/organizationCardUpdatedCacheHandler';

import {
    GetUserGroupsQuery,
    useResendWelcomingEmailToUsersMutation,
    useToggleOrganizationPaymentForUserMutation,
    useToggleUserGroupForUserMutation,
} from 'data/__generated__';
import { MinimalUser } from 'pages/Settings/SettingsPage/SettingsTeam';

interface EmployeesTableProps {
    users: MinimalUser[];
    refetchUsers: () => void;
    userGroups: GetUserGroupsQuery['userGroups'];
}

export const EmployeesTable: React.FC<EmployeesTableProps> = ({ users, refetchUsers, userGroups }) => {
    const apolloClient = useApolloClient() as ApolloClient<any>;

    const [employeePopupOpen, setEmployeePopupOpen] = useState<boolean>(false);
    const [addEmployeesPopupOpen, setAddEmployeesPopupOpen] = useState<boolean>(false);
    const [warningPopupOpen, setWarningPopupOpen] = useState<boolean>(false);
    const [userToDetach, setUserToDetach] = useState<MinimalUser | null>(null);
    const [resentEmailsAddresses, setResentEmailsAddresses] = useState<string[]>([]);
    const usersLength = users?.length;

    const [resendWelcomingEmailToUsers] = useResendWelcomingEmailToUsersMutation();
    const [toggleOrganizationPaymentForUser] = useToggleOrganizationPaymentForUserMutation();
    const [toggleUserGroupForUser] = useToggleUserGroupForUserMutation();

    const handleDetachWarning = (userId: string | undefined) => {
        setUserToDetach(users?.find((user) => user?._id === userId) || null);
        setWarningPopupOpen(true);
    };

    function toggleOrganizationPaymentCallback({
        userId,
        isOrganizationPaymentEnabled,
    }: {
        userId: string | undefined;
        isOrganizationPaymentEnabled: boolean;
    }) {
        return async () => {
            if (!userId) return;
            const { data } = await toggleOrganizationPaymentForUser({
                variables: { userId, isOrganizationPaymentEnabled },
            });

            const { errors, success, organizationCard } = data?.toggleOrganizationPaymentForUser || {};
            if (success && organizationCard) {
                organizationCardUpdatedCacheHandler({ apolloClient, organizationCard });
                toast.success("L'utilisateur·rice a bien été mis·e à jour.", {
                    duration: 4000,
                });
            } else if (errors) {
                errors.forEach((error: string) => toast.error(error));
            }
        };
    }

    function toggleUserGroupCallback({
        userId,
        userGroupId,
        isUserGroupEnabled,
    }: {
        userId: string | undefined;
        userGroupId: string;
        isUserGroupEnabled: boolean;
    }) {
        return async () => {
            if (!userId) return;
            const { data } = await toggleUserGroupForUser({
                variables: { userId, userGroupId, isUserGroupEnabled },
            });
            const { errors, success } = data?.toggleUserGroupForUser || {};
            if (success) {
                // organizationCardUpdatedCacheHandler({ apolloClient, organizationCard });
                toast.success("L'utilisateur·rice a bien été mis·e à jour.", {
                    duration: 4000,
                });
                // cache should be updated, but sometimes it's not
                refetchUsers();
            } else if (errors) {
                errors.forEach((error: string) => toast.error(error));
            }
        };
    }

    const handleResendMails = async (emailAddresses: string[]) => {
        const { data } = await resendWelcomingEmailToUsers({
            variables: { emailAddresses },
        });

        const response = (data?.resendWelcomingEmailToUsers || []).filter(
            (email): email is string => typeof email === 'string',
        );
        setResentEmailsAddresses([...resentEmailsAddresses, ...response]);
    };

    const usersWithWithoutAnyVerifiedEmail = users?.filter(
        (user) => Array.isArray(user?.emails) && !user.emails.find((email) => email?.verified === true),
    );

    const allEmailsToResend = usersWithWithoutAnyVerifiedEmail
        ?.filter(
            (user) =>
                Array.isArray(user?.emails) &&
                user?.emails[0] &&
                user.emails[0].address &&
                !resentEmailsAddresses?.includes(user.emails[0].address),
        )
        .map((user) => (Array.isArray(user?.emails) && user.emails[0] ? user.emails[0].address : undefined));

    return (
        <>
            <EmployeesTitle>
                <SettingsCardSubtitle $noMargin>Employés {!!usersLength && `(${usersLength})`}</SettingsCardSubtitle>
                <OutsideClickTracker
                    render={(open, setOpen) => (
                        <EmployeesDropdownButton
                            open={open}
                            setOpen={setOpen}
                            setEmployeePopupOpen={setEmployeePopupOpen}
                            setAddEmployeesPopupOpen={setAddEmployeesPopupOpen}
                        />
                    )}
                />
            </EmployeesTitle>
            <EmployeeSectionExplanation>
                Si vous disposez d'un{' '}
                <a target="_blank" rel="noopener noreferrer" href="https://totem.co/stores/">
                    magasin
                </a>
                , et que vous souhaitez mettre en place des subventions pour vos collaborateurs, vous avez la
                possibilité d'ajouter ici leurs emails. Lorsqu'un utilisateur se créera un compte sur l'application
                mobile TOTEM avec un des emails renseignés, il bénéficiera automatiquement des subventions mises en
                place.
            </EmployeeSectionExplanation>
            {!!usersLength && (
                <InvoicesTableStyle $lessPadding style={{ textAlign: 'left' }}>
                    <thead>
                        <tr>
                            <th>Nom complet</th>
                            <th>Adresse e-mail (compte créé ou non)</th>
                            <th>Badge pro</th>
                            {userGroups.map(({ _id, label }) => (
                                <th key={_id}>{label}</th>
                            ))}
                            <th>Détacher de mon entreprise</th>
                            <th>
                                <AlignedTextAndIcon>
                                    <MailSendIcon
                                        size="20px"
                                        onClick={() =>
                                            handleResendMails(
                                                allEmailsToResend?.filter(
                                                    (email): email is string => typeof email === 'string',
                                                ),
                                            )
                                        }
                                        data-for="resendAllEmailsTooltip"
                                        data-tip="Renvoyer les invitations aux employés non-inscrits"
                                    />
                                    <MultilineTooltip
                                        id="resendAllEmailsTooltip"
                                        effect="solid"
                                        type="dark"
                                        place="bottom"
                                    />
                                </AlignedTextAndIcon>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {users?.map((user, index) => {
                            const userId = user?._id;
                            const lastSeenAt = user?.lastSeenAt ?? user?.lastSeenAt ?? null;
                            const profile = user?.profile ?? {};
                            const firstname = profile.firstname ?? '';
                            const lastname = profile.lastname ?? '';
                            const emails = Array.isArray(user?.emails) ? user.emails : [];
                            const userGroupIds = Array.isArray(user?.userGroupIds) ? user.userGroupIds : [];
                            const wallet = user?.wallet ?? {};
                            const organizationCard = wallet.organizationCard ?? null;
                            const emailAddress = emails.find((email) => email && email.verified) ||
                                emails[0] || { address: '', verified: false };
                            const displayResendEmailIcon =
                                emailAddress.address && !resentEmailsAddresses.includes(emailAddress.address);
                            const isNotAdmin = index > 0;
                            const hasOrganizationCard = !!organizationCard && organizationCard.state === 'Active';

                            return (
                                <tr key={userId}>
                                    <td>
                                        {firstname} {lastname}
                                    </td>
                                    <td>
                                        <AlignedTextAndIcon>
                                            {emailAddress.address}
                                            {emailAddress.verified ? (
                                                <GreenIconCircleCheck size={15} />
                                            ) : (
                                                <RedIconCircleX size={15} />
                                            )}
                                        </AlignedTextAndIcon>
                                    </td>
                                    <td>
                                        <Checkbox
                                            readOnly
                                            checked={hasOrganizationCard}
                                            onChange={toggleOrganizationPaymentCallback({
                                                userId,
                                                isOrganizationPaymentEnabled: !hasOrganizationCard,
                                            })}
                                        />
                                    </td>
                                    {userGroups.map(({ _id }) => (
                                        <td key={_id}>
                                            <Checkbox
                                                readOnly
                                                checked={userGroupIds.includes(_id)}
                                                onChange={toggleUserGroupCallback({
                                                    userId,
                                                    userGroupId: _id,
                                                    isUserGroupEnabled: !userGroupIds.includes(_id),
                                                })}
                                            />
                                        </td>
                                    ))}
                                    {isNotAdmin ? (
                                        <td>
                                            <DetachIcon size="20px" onClick={() => handleDetachWarning(userId)} />
                                        </td>
                                    ) : null}
                                    <td>
                                        <AlignedTextAndIcon>
                                            {emailAddress.verified && lastSeenAt ? (
                                                `En ligne ${dayjs(lastSeenAt).fromNow()}`
                                            ) : displayResendEmailIcon ? (
                                                <>
                                                    <MailSendIcon
                                                        size="20px"
                                                        onClick={() => handleResendMails([emailAddress.address])}
                                                        data-for="resendEmailTooltip"
                                                        data-tip="Renvoyer l'invitation"
                                                    />
                                                    <Tooltip
                                                        id="resendEmailTooltip"
                                                        effect="solid"
                                                        type="dark"
                                                        place="bottom"
                                                    />
                                                </>
                                            ) : (
                                                <GreenIconCircleCheck size="20px" />
                                            )}
                                        </AlignedTextAndIcon>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </InvoicesTableStyle>
            )}
            {warningPopupOpen && (
                <DetachEmployeeWarningPopup setWarningPopupOpen={setWarningPopupOpen} userToDetach={userToDetach} />
            )}
            {employeePopupOpen && <TeamPopupWindow setPopupOpen={setEmployeePopupOpen} isEmployee={true} />}
            {addEmployeesPopupOpen && (
                <AddEmployeesPopupWindow setPopupOpen={setAddEmployeesPopupOpen} refetchUsers={refetchUsers} />
            )}
        </>
    );
};

const EmployeeSectionExplanation = styled.p`
    font-size: 1.2em;
    margin-bottom: 2em;
    max-width: 1000px;
    a {
        text-decoration: underline;
    }
`;

const AlignedTextAndIcon = styled.div`
    display: flex;
    align-items: center;
    svg {
        margin-left: 5px;
    }
`;

const MailSendIcon = styled(IconMailForward)`
    cursor: pointer;
    color: ${(props) => props.theme.colors.black};
    transition: all 0.25s ease;

    &:hover {
        filter: brightness(85%);
    }
`;

const DetachIcon = styled(IconUnlink)`
    cursor: pointer;
    color: ${(props) => props.theme.colors.black};
    transition: all 0.25s ease;

    &:hover {
        filter: brightness(85%);
    }
`;

const RedIconCircleX = styled(IconCircleX)`
    color: ${(props) => props.theme.colors.red};
`;
const GreenIconCircleCheck = styled(IconCircleCheck)`
    color: ${(props) => props.theme.colors.green};
`;
